import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import Button from '@components/button/Button';
import CustomTable from '@components/custom-table/CustomTable';
import {
  CustomTableRowData,
  CustomTableSortOrder,
  CustomTableSortState
} from '@components/custom-table/types';
import Spinner from '@components/spinner/Spinner';
import { FEATURE_FLAGS } from '@constants/feature';
import { DEFAULT_PLACEHOLDER } from '@constants/generic';
import { LEAD_MANAGEMENT } from '@constants/routes';
import { useAppSelector } from '@core/store';
import { leadSourceOptions } from '@features/lead-management/constants';
import useCurrency from '@hooks/useCurrency';

import EmptyScreen from './EmptyScreen';
import SourceAvatar from './SourceAvatar';

import {
  getSourceDistributionCopy,
  getSourceDistributionTableColumns,
  SOURCES_PAGE_LIMIT
} from '../../constants/leadSources';
import {
  LeadAdSourceDistributionItem,
  LeadSourceDistributionItem
} from '../../types/leadSources';

interface SourceDistributionTableProps {
  sources: LeadSourceDistributionItem[] | LeadAdSourceDistributionItem[];
  sortState: CustomTableSortState | null;
  isAdSource?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  /** Pagination is only used for Ad Sources (API-supported). */
  totalCount?: number;
  page?: number;
  onPageChange?: (page: number) => void;
  onSortChange: (sortKey: string, sortOrder: CustomTableSortOrder) => void;
}

const formatConversionRate = (rate: number) => {
  const formatted = Number.isInteger(rate)
    ? String(rate)
    : rate.toFixed(2).replace(/\.?0+$/, '');
  return `${formatted}%`;
};

const SourceDistributionTable = ({
  sources,
  totalCount,
  page = 0,
  sortState,
  isAdSource = false,
  isLoading = false,
  isError = false,
  onPageChange,
  onSortChange
}: SourceDistributionTableProps) => {
  const { formatCurrencyValue } = useCurrency();
  const navigate = useNavigate();

  const orgFeatureFlags = useAppSelector(
    (state) => state.user.orgFeatureFlags
  ) as string[];
  const isMetaLeadIngestionEnabled = orgFeatureFlags?.includes(
    FEATURE_FLAGS.META_LEAD_INGESTION
  );

  const distributionCopy = getSourceDistributionCopy(isAdSource);
  const { accessor: nameAccessor } = distributionCopy.nameColumn;
  const enablePagination = isAdSource && !!onPageChange;

  const sourceDistributionTableColumns = useMemo(
    () => getSourceDistributionTableColumns(isAdSource),
    [isAdSource]
  );

  const rows = useMemo<CustomTableRowData[]>(
    () =>
      sources.map((source, index) => {
        const sourceNameLabel =
          leadSourceOptions[source.name as keyof typeof leadSourceOptions] ||
          source.name ||
          DEFAULT_PLACEHOLDER;
        const revenue = formatCurrencyValue(source.revenue / 100);
        const adId =
          'ad_id' in source && source.ad_id ? source.ad_id : undefined;
        const nameCell = isAdSource ? (
          <span>{sourceNameLabel}</span>
        ) : (
          <div className="flex min-w-0 items-center gap-2">
            <SourceAvatar name={source.name} compact />
            <span className="truncate">{sourceNameLabel}</span>
          </div>
        );
        return {
          id: adId ?? `${sourceNameLabel}-${index}`,
          [nameAccessor]: nameCell,
          leads: source.leads.toLocaleString() || DEFAULT_PLACEHOLDER,
          converted: source.converted.toLocaleString() || DEFAULT_PLACEHOLDER,
          conversionRate:
            formatConversionRate(source.conversion_rate) || DEFAULT_PLACEHOLDER,
          revenue: (
            <span>
              <span className="font-inter">{revenue.symbol}</span>{' '}
              {revenue.value}
            </span>
          )
        };
      }),
    [sources, formatCurrencyValue, nameAccessor, isAdSource]
  );

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex w-full items-center justify-center py-16">
          <Spinner size="lg" />
        </div>
      );
    }

    if (isError) {
      return (
        <EmptyScreen
          title={distributionCopy.errorTitle}
          description={distributionCopy.errorDescription}
        />
      );
    }

    if (sources.length === 0) {
      return (
        <EmptyScreen
          title={distributionCopy.emptyTitle}
          description={distributionCopy.emptyDescription}
        />
      );
    }

    return (
      <CustomTable
        headers={sourceDistributionTableColumns}
        data={rows}
        enableSort
        sortState={sortState}
        onSortChange={onSortChange}
        showGoToPage
        {...(enablePagination && {
          currentPage: page + 1,
          totalCount,
          onPageChange: (nextPage: number) => onPageChange?.(nextPage - 1),
          paginationLimit: SOURCES_PAGE_LIMIT
        })}
      />
    );
  };

  return (
    <div className="flex w-full flex-col gap-4 pt-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-semibold text-cofeeNeutral10">
          {distributionCopy.title}
        </h2>
        {!isAdSource && isMetaLeadIngestionEnabled && (
          <Button
            variant="tertiary"
            size="medium"
            label={distributionCopy.viewAdSources}
            labelStyle="!text-base !font-medium !text-cofeeNeutral10"
            rightIconName="arrow-right"
            rightIconStyle="!size-5 !text-cofeeGreyBlue7"
            handleClick={() => navigate(LEAD_MANAGEMENT.LEAD_AD_SOURCES)}
          />
        )}
      </div>
      {renderBody()}
    </div>
  );
};

export default SourceDistributionTable;
